import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Card,
  Typography,
  Tag,
  Progress,
  Divider,
  Alert,
  Tabs,
  message,
  Spin,
  Row,
  Col,
  Tooltip,
  Badge,
} from "antd";
import {
  ThunderboltOutlined,
  MessageOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  SendOutlined,
  TruckOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import api from "../../../shared/api.jsx";
import { getQuoteById } from "../../quotes/services/quoteService";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const { TextArea } = Input;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

const MatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%);
  border-radius: 8px;
`;

const RouteBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #005249;
`;

const RevenueDisplay = styled.div`
  text-align: right;
  
  .label {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
  }
  
  .amount {
    font-size: 24px;
    font-weight: 700;
    color: #52c41a;
  }
`;

const LoadCard = styled(Card)`
  margin-bottom: 16px;
  border-left: 4px solid ${(props) => props.$color || "#1890ff"};
  
  .ant-card-head {
    background: ${(props) => props.$bgColor || "#f0f5ff"};
    border-bottom: 1px solid ${(props) => props.$color || "#1890ff"};
  }
  
  .ant-card-head-title {
    font-weight: 600;
    color: ${(props) => props.$color || "#1890ff"};
  }
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const LoadDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin: 12px 0;
`;

const DetailItem = styled.div`
  .label {
    font-size: 11px;
    color: #999;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  
  .value {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }
`;

const MessagePreview = styled.div`
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  font-family: "Courier New", monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const CombinedFillDisplay = styled.div`
  text-align: center;
  padding: 16px;
  background: #f6ffed;
  border-radius: 8px;
  margin-bottom: 24px;
  
  .fill-percentage {
    font-size: 32px;
    font-weight: 700;
    color: #52c41a;
    margin: 8px 0;
  }
  
  .fill-label {
    font-size: 14px;
    color: #666;
  }
`;

const MatchAndQuoteModal = ({
  visible,
  matchData,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const combinedCost = Form.useWatch("combinedCost", form);
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [fetchingQuotes, setFetchingQuotes] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [messagePreviews, setMessagePreviews] = useState({});
  const [sending, setSending] = useState(false);
  const [sentQuotes, setSentQuotes] = useState(new Set());

  useEffect(() => {
    if (visible && matchData?.quoteIds) {
      fetchQuotes();
    } else {
      setQuotes([]);
      setMessagePreviews({});
      setSentQuotes(new Set());
    }
  }, [visible, matchData]);

  const fetchQuotes = async () => {
    if (!matchData?.quoteIds || matchData.quoteIds.length === 0) return;

    setFetchingQuotes(true);
    try {
      const quotePromises = matchData.quoteIds.map((id) => getQuoteById(id));
      const results = await Promise.all(quotePromises);

      const loadedQuotes = results
        .filter((r) => r.success && r.data)
        .map((r) => r.data);

      setQuotes(loadedQuotes);

      const previews = {};
      loadedQuotes.forEach((quote) => {
        previews[quote.id] = generateMessagePreview(quote);
      });
      setMessagePreviews(previews);

      const combinedCost = loadedQuotes.reduce(
        (sum, q) => sum + (q.estimatedCost || 0),
        0
      );
      const commonDates = findCommonDates(loadedQuotes);

      form.setFieldsValue({
        combinedCost,
        moveDate: commonDates[0] || "",
        customMessage: generateDefaultMessage(loadedQuotes),
      });
    } catch (error) {
      logError("Error fetching quotes", { error: error.message });
      message.error("Failed to load quote details");
    } finally {
      setFetchingQuotes(false);
    }
  };

  const findCommonDates = (quotes) => {
    if (quotes.length === 0) return [];
    if (quotes.length === 1) return quotes[0].dates || [quotes[0].date] || [];

    const allDates = quotes.map((q) => q.dates || [q.date]).flat();
    const dateCounts = {};

    allDates.forEach((date) => {
      dateCounts[date] = (dateCounts[date] || 0) + 1;
    });

    return Object.keys(dateCounts)
      .filter((date) => dateCounts[date] === quotes.length)
      .sort();
  };

  const generateDefaultMessage = (quotes) => {
    if (quotes.length === 0) return "";

    const route = `${quotes[0].from} → ${quotes[0].to}`;
    return `Great news! We've found a Shared load opportunity for your move from ${route}. This means you'll save money while still getting reliable service.`;
  };

  const generateMessagePreview = (quote) => {
    const customerName = quote.customerName || "there";
    const from = quote.from || "Pickup location";
    const to = quote.to || "Delivery location";
    const cost = quote.estimatedCost || 0;
    const formattedCost = cost.toLocaleString("en-ZA");

    let msg = `Good day ${customerName}! 👋\n\n`;
    msg += `Here's your *Shared load* quote for the move from ${from} → ${to}\n\n`;
    msg += `💰 *Shared load Price: R ${formattedCost}*\n\n`;
    msg += `*Your Shared load Includes:*\n`;
    msg += `• Protective blanket wrapping\n`;
    msg += `• 2 trained movers\n`;
    msg += `• Insurance cover\n`;
    msg += `• Loading and offloading\n\n`;
    msg += `*Payment Terms:*\n`;
    msg += `• 20% deposit: R ${(cost * 0.2).toLocaleString("en-ZA")}\n`;
    msg += `• Balance on delivery: R ${(cost * 0.8).toLocaleString("en-ZA")}\n\n`;
    msg += `*What's next?*\n`;
    msg += `• Reply *ACCEPT* to confirm\n`;
    msg += `• Reply *CHANGE* to request modifications\n`;
    msg += `• Reply *CALL* to speak with us\n\n`;
    msg += `Thank you for choosing Bubakii! 🚚`;

    return msg;
  };

  const handleSendQuote = async (quoteId, sendToAll = false) => {
    const quote = quotes.find((q) => q.id === quoteId);
    if (!quote) return;

    setSending(true);
    try {
      const formValues = form.getFieldsValue();
      const message = formValues.customMessage || messagePreviews[quoteId];

      const response = await api.post(`/quotes/${quoteId}/send`, {
        message,
        estimatedCost: sendToAll ? formValues.combinedCost : quote.estimatedCost,
      });

      if (response.success) {
        setSentQuotes((prev) => new Set([...prev, quoteId]));
        message.success(`Quote sent to ${quote.customerName || "customer"}`);

        if (sendToAll && quotes.length > 1) {
          const remaining = quotes.filter((q) => q.id !== quoteId);
          for (const remainingQuote of remaining) {
            if (!sentQuotes.has(remainingQuote.id)) {
              await handleSendQuote(remainingQuote.id, false);
            }
          }
        }

        if (onSuccess) {
          onSuccess({ quoteId, sent: true });
        }
      } else {
        message.error(response.error || "Failed to send quote");
      }
    } catch (error) {
      logError("Error sending quote", { error: error.message, quoteId });
      message.error("Failed to send quote. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleSendAll = async () => {
    const unsent = quotes.filter((q) => !sentQuotes.has(q.id));
    if (unsent.length === 0) {
      message.info("All quotes have been sent");
      return;
    }

    setSending(true);
    try {
      const formValues = form.getFieldsValue();
      const matchId = matchData?.id || `${quotes[0]?.id}_${quotes[1]?.id}`;
      const quoteIds = unsent.map((q) => q.id);

      const response = await api.post(`/share-load/match/${matchId}/quote`, {
        quoteIds,
        customMessage: formValues.customMessage || generateDefaultMessage(quotes),
        combinedCost: formValues.combinedCost,
      });

      if (response.success) {
        const sentIds = new Set(quoteIds);
        setSentQuotes((prev) => new Set([...prev, ...sentIds]));
        message.success(
          `Successfully sent ${response.sent || sentIds.size} of ${response.total || sentIds.size} quotes`
        );

        if (onSuccess) {
          onSuccess({ sent: response.sent || sentIds.size, total: response.total || sentIds.size });
        }
      } else {
        message.error(response.error || "Failed to send combined quotes");
      }
    } catch (error) {
      logError("Error sending combined quotes", { error: error.message });
      message.error("Failed to send quotes. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleWhatsApp = (phone) => {
    if (!phone) {
      message.warning("Phone number not available");
      return;
    }
    const whatsappUrl = `https://wa.me/${phone.replace(/[^0-9]/g, "")}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleCall = (phone) => {
    if (!phone) {
      message.warning("Phone number not available");
      return;
    }
    window.location.href = `tel:${phone}`;
  };

  const handleCopyMessage = async (quoteId) => {
    const messageText = messagePreviews[quoteId] || "";
    if (!messageText) return;

    try {
      await navigator.clipboard.writeText(messageText);
      message.success("Message copied to clipboard!");
    } catch (error) {
      message.error("Failed to copy message");
    }
  };

  const getFillColor = (percentage) => {
    if (percentage >= 80) return "#52c41a";
    if (percentage >= 60) return "#faad14";
    return "#ff4d4f";
  };

  const combinedFill = matchData?.combinedFill || 0;
  const allSent = quotes.length > 0 && quotes.every((q) => sentQuotes.has(q.id));

  return (
    <Modal
      title={
        <Space>
          <ThunderboltOutlined style={{ color: "#005249" }} />
          <span>Match & Send Quotes</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnHidden
      maskClosable={!loading && !sending}
    >
      {fetchingQuotes ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: "#666" }}>
            Loading quote details...
          </div>
        </div>
      ) : quotes.length === 0 ? (
        <Alert
          message="No quotes found"
          description="Unable to load quote details. Please try again."
          type="error"
        />
      ) : (
        <Form form={form} layout="vertical">
          {/* Match Header */}
          <MatchHeader>
            <div>
              <RouteBadge>
                <TruckOutlined />
                <span>{matchData?.route || `${quotes[0]?.from} → ${quotes[0]?.to}`}</span>
              </RouteBadge>
              <div style={{ marginTop: 8, fontSize: "13px", color: "#666" }}>
                {quotes.length} load{quotes.length > 1 ? "s" : ""} • Combined move
              </div>
            </div>
            <RevenueDisplay>
              <div className="label">Potential Revenue</div>
              <div className="amount">
                R{(combinedCost || matchData?.potentialRevenue || 0).toLocaleString()}
              </div>
            </RevenueDisplay>
          </MatchHeader>

          {/* Combined Fill Display */}
          <CombinedFillDisplay>
            <div className="fill-label">Combined Truck Fill</div>
            <div className="fill-percentage">{combinedFill}%</div>
            <Progress
              percent={combinedFill}
              strokeColor={getFillColor(combinedFill)}
              showInfo={false}
              style={{ maxWidth: "300px", margin: "0 auto" }}
            />
            <div style={{ marginTop: 8, fontSize: "12px", color: "#999" }}>
              {combinedFill >= 80
                ? "Excellent fill rate"
                : combinedFill >= 60
                ? "Good fill rate"
                : "Could use more capacity"}
            </div>
          </CombinedFillDisplay>

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            {/* Overview Tab */}
            <TabPane
              tab={
                <Space>
                  <TruckOutlined />
                  <span>Overview</span>
                </Space>
              }
              key="overview"
            >
              <Form.Item
                label={
                  <Space>
                    <DollarOutlined />
                    <span>Combined Cost (R)</span>
                  </Space>
                }
                name="combinedCost"
                rules={[{ required: true, message: "Combined cost is required" }]}
              >
                <Input
                  type="number"
                  prefix="R"
                  style={{ fontSize: "16px" }}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    form.setFieldsValue({ combinedCost: value });
                  }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    <CalendarOutlined />
                    <span>Move Date</span>
                  </Space>
                }
                name="moveDate"
                rules={[{ required: true, message: "Move date is required" }]}
              >
                <Input type="date" />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    <MessageOutlined />
                    <span>Custom Message (Optional)</span>
                  </Space>
                }
                name="customMessage"
              >
                <TextArea
                  rows={4}
                  placeholder="Add a custom message that will be included with each quote..."
                />
              </Form.Item>

              <Divider>Matched Loads</Divider>

              {quotes.map((quote, idx) => {
                const isSent = sentQuotes.has(quote.id);
                const colors = ["#1890ff", "#52c41a", "#faad14"];
                const color = colors[idx % colors.length];

                return (
                  <LoadCard
                    key={quote.id}
                    $color={color}
                    $bgColor={isSent ? "#f6ffed" : "#f0f5ff"}
                    title={
                      <Space>
                        <Badge
                          status={isSent ? "success" : "default"}
                          text={`Load ${idx + 1}: ${quote.id}`}
                        />
                        {isSent && (
                          <Tag color="success" icon={<CheckCircleOutlined />}>
                            Sent
                          </Tag>
                        )}
                      </Space>
                    }
                    extra={
                      <Space>
                        <Tooltip title="Call customer">
                          <Button
                            size="small"
                            icon={<PhoneOutlined />}
                            onClick={() => handleCall(quote.customerPhone)}
                          />
                        </Tooltip>
                        <Tooltip title="Open WhatsApp">
                          <Button
                            size="small"
                            icon={<MessageOutlined />}
                            onClick={() => handleWhatsApp(quote.customerPhone)}
                          />
                        </Tooltip>
                      </Space>
                    }
                  >
                    <CustomerInfo>
                      <UserOutlined style={{ fontSize: "18px", color: color }} />
                      <div>
                        <div style={{ fontWeight: 600 }}>{quote.customerName || "Unknown"}</div>
                        <div style={{ fontSize: "12px", color: "#999" }}>
                          {quote.customerPhone || "No phone"}
                        </div>
                      </div>
                    </CustomerInfo>

                    <LoadDetails>
                      <DetailItem>
                        <div className="label">From</div>
                        <div className="value">{quote.from}</div>
                      </DetailItem>
                      <DetailItem>
                        <div className="label">To</div>
                        <div className="value">{quote.to}</div>
                      </DetailItem>
                      <DetailItem>
                        <div className="label">Load Type</div>
                        <div className="value">
                          {quote.loadType?.replace("_", " ").toUpperCase() || "N/A"}
                        </div>
                      </DetailItem>
                      <DetailItem>
                        <div className="label">Original Cost</div>
                        <div className="value">R{quote.estimatedCost?.toLocaleString() || "0"}</div>
                      </DetailItem>
                    </LoadDetails>

                    <ActionButtons>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={() => handleSendQuote(quote.id)}
                        loading={sending}
                        disabled={isSent}
                      >
                        {isSent ? "Sent" : "Send Quote"}
                      </Button>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyMessage(quote.id)}
                      >
                        Copy Message
                      </Button>
                    </ActionButtons>
                  </LoadCard>
                );
              })}
            </TabPane>

            {/* Message Preview Tab */}
            <TabPane
              tab={
                <Space>
                  <MessageOutlined />
                  <span>Message Previews</span>
                </Space>
              }
              key="preview"
            >
              {quotes.map((quote, idx) => (
                <Card
                  key={quote.id}
                  title={
                    <Space>
                      <UserOutlined />
                      <span>{quote.customerName || "Customer"} ({quote.id})</span>
                    </Space>
                  }
                  style={{ marginBottom: 16 }}
                  extra={
                    <Space>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyMessage(quote.id)}
                      >
                        Copy
                      </Button>
                      <Button
                        size="small"
                        icon={<MessageOutlined />}
                        onClick={() => handleWhatsApp(quote.customerPhone)}
                      >
                        WhatsApp
                      </Button>
                    </Space>
                  }
                >
                  <MessagePreview>
                    {messagePreviews[quote.id] || "Generating preview..."}
                  </MessagePreview>
                </Card>
              ))}
            </TabPane>
          </Tabs>

          {/* Footer Actions */}
          <Divider />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              {allSent && (
                <Alert
                  message="All quotes sent successfully!"
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
            </div>
            <Space>
              <Button onClick={onClose} disabled={sending}>
                {allSent ? "Close" : "Cancel"}
              </Button>
              {!allSent && (
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={handleSendAll}
                  loading={sending}
                  disabled={quotes.length === 0}
                >
                  Send All Quotes
                </Button>
              )}
            </Space>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default MatchAndQuoteModal;

